import React, { useState, useEffect, Suspense, lazy } from 'react'
import Modal from 'react-modal'
import ExportQuest from '../modals/ExportQuest'
import ImportText from '../modals/ImportText'
import VideoRecorder from '../answers/VideoRecorder'
import ScreenRecorder from '../answers/ScreenRecorder'
import Reload from '../modals/Reload'
import NewStream from '../modals/NewStream'
import NewSpace from 'components/modals/NewSpace'
import UserImageUpdate from 'components/modals/UserImageUpdate'
import SpaceImageUpdate from 'components/modals/SpaceImageUpdate'
import StreamImageUpdate from 'components/modals/StreamImageUpdate'
import useStore from 'state/knovStore'
import HandCashError from '../modals/HandCashError'
import styles from 'components/shared/modal.module.scss'
import cn from 'classnames'
import { isMobile } from 'react-device-detect'
import Wallet from 'components/modals/Wallet'
import { Wallet as WalletIcon } from 'lucide-react'
import OrdinalsIcon from './OrdinalsIcon'
import Confirm from 'components/quests/Confirm'
import EditProfile from 'components/users/EditProfile'
import OrdinalsViewer from 'components/modals/OrdinalsViewer'
import TeamSettings from 'components/teams/TeamSettings'
import CreateStreamModal from 'components/modals/CreateStreamModal'
import NewAnswer from 'components/answers/NewAnswer'
import useKeyboardHeight from 'hooks/useKeyboardHeight'

// Lazy loaded components
const MetricsViewer = lazy(() => import('components/modals/MetricsViewer'))

const CUSTOM_MODAL_ICONS = {
    WalletIcon: <WalletIcon size={40} />,
    OrdinalsIcon: <OrdinalsIcon size={40} />,
}

Modal.setAppElement('#knovigator-body')

// Setting top does not work bc react-modal uses inset property to control top margin.
const customStyles = {
    content: {
        left: '50%',
        right: 'auto',
        bottom: 'auto',
        marginRight: '-50%',
        transform: 'translate(-50%, 0%)',
        width: '50dvw',
        maxWidth: '50dvw',
        maxHeight: '80dvh',
        padding: 0,
        borderRadius: '8px',
    },
}

const mobileCustomStyles = {
    content: {
        width: '95dvw',
        maxWidth: '95dvw',
        maxHeight: '80dvh',
        padding: 0,
    },
}

export default function KnovModal(props) {
    const isModalOpen = useStore(state => state.isModalOpen)
    const closeModal = useStore(state => state.actions.modalActions.closeModal)
    const modalParams = useStore(state => state.modalParams) || props.modalParams
    const { keyboardHeight, isKeyboardVisible } = useKeyboardHeight()

    const logoSize = '36px'
    const hiddenModal = modalParams && modalParams.hidden

    const mobileStyles = isMobile ? styles.mobileKnovModal : null
    const mobileRemoveBtnStyles = isMobile ? styles.mobileRemoveBtn : null

    // Calculate available height when keyboard is visible
    const [modalHeight, setModalHeight] = useState('80vh')

    useEffect(() => {
        if (modalParams?.type === 'NewAnswer' && isMobile) {
            if (isKeyboardVisible) {
                // Calculate available height above keyboard
                const viewportHeight = window.innerHeight
                const availableHeight = viewportHeight - keyboardHeight - 50 // 50px for safety margin
                setModalHeight(`${availableHeight}px`)
            } else {
                setModalHeight('80vh')
            }
        }
    }, [isKeyboardVisible, keyboardHeight, modalParams?.type])

    const mergedStyles = {
        overlay: {
            // Adjust overlay for keyboard on mobile
            ...(modalParams?.type === 'NewAnswer' &&
                isMobile &&
                isKeyboardVisible && {
                    position: 'fixed',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    backgroundColor: 'rgba(0, 0, 0, 0.75)',
                    zIndex: 999999999,
                    display: 'flex',
                    alignItems: 'flex-start',
                    paddingTop: '20px',
                }),
        },
        content: {
            ...customStyles.content,
            ...(isMobile && mobileCustomStyles.content),
            ...(modalParams?.customStyles ? modalParams.customStyles : {}),
            ...(isMobile && modalParams?.mobileCustomStyles ? modalParams.mobileCustomStyles : {}),
            // Special handling for NewAnswer modal to ensure proper scrolling
            ...(modalParams?.type === 'NewAnswer'
                ? {
                      display: 'flex',
                      flexDirection: 'column',
                      overflow: 'hidden',
                      maxHeight: modalHeight,
                      // Use absolute positioning for better Android WebView support
                      ...(isMobile && {
                          position: 'relative',
                          top: 'auto',
                          transform: 'none',
                          left: '0',
                          right: '0',
                          margin: '0 auto',
                          width: '100%',
                          maxWidth: '100%',
                          ...(isKeyboardVisible && {
                              maxHeight: `${window.innerHeight - keyboardHeight - 40}px`,
                              height: 'auto',
                          }),
                      }),
                  }
                : {}),
        },
    }

    return hiddenModal ? (
        <div style={{ height: 0, width: 0 }}>{getModalContent(modalParams, closeModal)}</div>
    ) : (
        <Modal
            isOpen={isModalOpen}
            onRequestClose={closeModal}
            style={mergedStyles}
            contentLabel="Knov Modal"
        >
            <div
                id="knov-modal"
                className={cn(
                    'knov-modal-comp',
                    styles.knovModal,
                    mobileStyles,
                    modalParams?.type === 'NewAnswer' && isMobile && styles.mobileNewAnswerModal,
                )}
            >
                {!modalParams?.noHeader && (
                    <div
                        className={cn(
                            styles.knovModalHeader,
                            modalParams?.type === 'Wallet' && styles.walletHeader,
                        )}
                    >
                        <div className={styles.knovModalBrand}>
                            {modalParams?.icon && (
                                <div className={styles.modalIcon}>
                                    {
                                        // use custom modal icon if specified, otherwise fallback to fa icon
                                        CUSTOM_MODAL_ICONS[modalParams?.icon] || (
                                            <div className={styles.fontIcon}>
                                                <i className={modalParams.icon} />
                                            </div>
                                        )
                                    }
                                </div>
                            )}
                            {modalParams?.title && (
                                <span className={styles.modalTitle}>{modalParams.title}</span>
                            )}
                        </div>
                    </div>
                )}
                <div className={cn(styles.removeBtn, mobileRemoveBtnStyles)} onClick={closeModal}>
                    <i className="fa fa-close fa-lg"></i>
                </div>
                <div
                    className={cn(
                        'knov-modal-content',
                        styles.knovModalContent,
                        modalParams?.type === 'NewAnswer' && styles.newAnswerModalContent,
                    )}
                >
                    {getModalContent(modalParams, closeModal)}
                </div>
            </div>
        </Modal>
    )
}

// Wrapper component to handle focusing NewAnswer when modal opens
function NewAnswerWithFocus({ modalParams, closeModal }) {
    const newAnswerRef = React.useRef(null)
    const { isKeyboardVisible } = useKeyboardHeight()

    React.useEffect(() => {
        // Focus the editor after a short delay to ensure it's mounted
        const timer = setTimeout(() => {
            if (newAnswerRef.current && newAnswerRef.current.focus) {
                newAnswerRef.current.focus()
            }
        }, 100)

        return () => clearTimeout(timer)
    }, [])

    return (
        <div
            className={cn(
                styles.newAnswerModalWrapper,
                isKeyboardVisible && styles.keyboardVisible,
            )}
        >
            <NewAnswer {...modalParams} closeModal={closeModal} ref={newAnswerRef} />
        </div>
    )
}

function getModalContent(modalParams, closeModal) {
    if (!modalParams) return null

    switch (modalParams.type) {
        case 'ExportQuest':
            return <ExportQuest modalParams={modalParams} closeModal={closeModal} />
        case 'ImportText':
            return <ImportText modalParams={modalParams} closeModal={closeModal} />
        case 'VideoRecorder':
            return <VideoRecorder modalParams={modalParams} closeModal={closeModal} />
        case 'ScreenRecorder':
            return <ScreenRecorder modalParams={modalParams} closeModal={closeModal} />
        case 'Reload':
            return <Reload modalParams={modalParams} closeModal={closeModal} />
        case 'NewStream':
            return <NewStream modalParams={modalParams} closeModal={closeModal} />
        case 'NewSpace':
            return <NewSpace modalParams={modalParams} closeModal={closeModal} />
        case 'SpaceImageUpdate':
            return <SpaceImageUpdate modalParams={modalParams} closeModal={closeModal} />
        case 'UserImageUpdate':
            return <UserImageUpdate modalParams={modalParams} closeModal={closeModal} />
        case 'StreamImageUpdate':
            return <StreamImageUpdate modalParams={modalParams} closeModal={closeModal} />
        case 'HandCashError':
            return <HandCashError modalParams={modalParams} closeModal={closeModal} />
        case 'Message':
            return <div>{modalParams.message}</div>
        case 'Wallet':
            return <Wallet modalParams={modalParams} closeModal={closeModal} />
        case 'OrdinalsViewer':
            return <OrdinalsViewer modalParams={modalParams} closeModal={closeModal} />
        case 'MetricsViewer':
            return (
                <Suspense
                    fallback={
                        <div
                            className="loading-fallback"
                            style={{
                                display: 'flex',
                                justifyContent: 'center',
                                alignItems: 'center',
                                height: '50vh',
                                flexDirection: 'column',
                            }}
                        >
                            <div style={{ marginBottom: '20px' }}>
                                <i className="fa fa-circle-o-notch fa-spin fa-2x" />
                            </div>
                            <div>Loading metrics...</div>
                        </div>
                    }
                >
                    <MetricsViewer modalParams={modalParams} closeModal={closeModal} />
                </Suspense>
            )
        case 'Confirm':
            return <Confirm modalParams={modalParams} closeModal={closeModal} />
        case 'EditProfile':
            return <EditProfile modalParams={modalParams} closeModal={closeModal} />
        case 'StreamSettings':
            return (
                <TeamSettings
                    team={modalParams.team}
                    teamId={modalParams.team.id}
                    closeModal={closeModal}
                />
            )
        case 'CreateStream':
            return (
                <CreateStreamModal
                    panelId={modalParams.panelId}
                    filter={modalParams.filter}
                    closeModal={closeModal}
                />
            )
        case 'NewAnswer':
            return <NewAnswerWithFocus modalParams={modalParams} closeModal={closeModal} />
    }
}
